import { AddItemToOrderModalProps } from '@/renderer/types/sales';
import { Loader2, Minus, NotebookText, Plus, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../common';

export const AddItemToOrderModal: React.FC<AddItemToOrderModalProps> = ({
  isOpen,
  item,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setQuantity(1);
    setNotes('');
  }, [isOpen, item?.id]);

  const total = useMemo(() => {
    if (!item) return 0;
    return item.base_price * quantity;
  }, [item, quantity]);

  if (!isOpen || !item) return null;

  const submitDisabled = isSubmitting || quantity <= 0;

  const handleConfirm = async () => {
    if (submitDisabled) return;
    await onSubmit({ notes, quantity });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isSubmitting ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close add item modal"
      />

      <div className="relative mx-4 w-full max-w-2xl overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 pb-4 pt-5">
          <h2 className="text-lg font-semibold leading-tight text-black uppercase">
            {item.name}
          </h2>

          <div className="flex items-center gap-4">
            <p className="text-lg font-semibold leading-none text-black">
              {total.toLocaleString()}
            </p>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
              aria-label="Close modal"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="space-y-5 px-6 pb-6 pt-5">
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-800">Quantity</p>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                disabled={isSubmitting}
                size="xs"
              >
                <Minus size={18} />
              </Button>

              <input
                type="number"
                min={1}
                value={quantity}
                disabled={isSubmitting}
                onChange={(event) => {
                  const value = Number.parseInt(event.target.value, 10);
                  if (Number.isNaN(value)) {
                    setQuantity(1);
                    return;
                  }
                  setQuantity(Math.max(1, value));
                }}
                className="h-10 w-20 rounded border border-gray-300 px-3 text-center text-sm font-semibold text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />

              <Button
                onClick={() => setQuantity((value) => value + 1)}
                disabled={isSubmitting}
                size="xs"
              >
                <Plus size={18} />
              </Button>
            </div>
          </div>

          {/* <div className="rounded border border-gray-300 bg-gray-50 p-3">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
              Sides
            </p>
            <button
              type="button"
              disabled
              className="inline-flex h-10 items-center gap-2 rounded border border-gray-300 bg-white px-4 text-sm text-gray-500"
            >
              <ChevronDown size={16} />
              Add
            </button>
          </div> */}

          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
              <NotebookText size={16} />
              Notes
            </p>
            <textarea
              value={notes}
              disabled={isSubmitting}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Add notes"
              rows={5}
              className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            />
          </div>

          <Button
            type="button"
            onClick={handleConfirm}
            disabled={submitDisabled}
            className="h-10 text-xs font-semibold uppercase tracking-wide"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Adding
              </>
            ) : (
              'Add'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
